import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Box,
  Menu,
  MenuItem,
  Avatar,
  Divider,
  ListItemIcon,
  Chip,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Logout,
  Person,
} from '@mui/icons-material';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import type { UserRole } from '@/types/user.types';

interface HeaderProps {
  onMenuClick?: () => void;
}

const roleLabels: Record<UserRole, string> = {
  admin: '最高管理員',
  sub_admin: '群組管理員',
  operator: '操作員',
};

const roleColors: Record<UserRole, 'error' | 'warning' | 'info'> = {
  admin: 'error',
  sub_admin: 'warning',
  operator: 'info',
};

export default function Header({ onMenuClick }: HeaderProps) {
  const { user, logout } = useAuth();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const navigate = useNavigate();

  const isMenuOpen = Boolean(anchorEl);

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    handleClose();
    await logout();
  };

  /**
   * Get user initials for avatar
   * @returns First letter of username in uppercase
   */
  const getUserInitial = () => {
    return user?.username?.charAt(0).toUpperCase() || 'U';
  };

  return (
    <AppBar position="fixed" sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}>
      <Toolbar>
        <IconButton
          color="inherit"
          edge="start"
          onClick={onMenuClick}
          sx={{ mr: 2, display: { sm: 'none' } }}
        >
          <MenuIcon />
        </IconButton>

        <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
          {import.meta.env.VITE_APP_NAME || '備份系統'}
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {/* Role badge - hidden on mobile */}
          {user?.role && (
            <Chip
              label={roleLabels[user.role]}
              color={roleColors[user.role]}
              size="small"
              sx={{
                display: { xs: 'none', sm: 'inline-flex' },
                fontWeight: 600,
                fontSize: '0.75rem',
              }}
            />
          )}

          {/* User name - hidden on mobile */}
          <Typography
            variant="body2"
            sx={{
              display: { xs: 'none', sm: 'block' },
              mr: 1,
            }}
          >
            {user?.username}
          </Typography>

          {/* User avatar button */}
          <IconButton
            onClick={handleMenu}
            size="small"
            aria-controls={isMenuOpen ? 'user-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={isMenuOpen ? 'true' : undefined}
          >
            <Avatar
              sx={{
                width: 32,
                height: 32,
                bgcolor: 'secondary.main',
                fontSize: '0.875rem',
              }}
            >
              {getUserInitial()}
            </Avatar>
          </IconButton>

          {/* User menu */}
          <Menu
            id="user-menu"
            anchorEl={anchorEl}
            open={isMenuOpen}
            onClose={handleClose}
            onClick={handleClose}
            slotProps={{
              paper: {
                elevation: 3,
                sx: {
                  minWidth: 200,
                  mt: 1.5,
                  '& .MuiMenuItem-root': {
                    px: 2,
                    py: 1,
                  },
                },
              },
            }}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          >
            {/* User info */}
            <Box sx={{ px: 2, py: 1.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                {user?.username}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {user?.email}
              </Typography>
              {user?.role && (
                <Box sx={{ mt: 0.5 }}>
                  <Chip
                    label={roleLabels[user.role]}
                    color={roleColors[user.role]}
                    size="small"
                    sx={{ fontSize: '0.7rem' }}
                  />
                </Box>
              )}
              {user?.primary_group && (
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                  群組：{user.primary_group.name}
                </Typography>
              )}
            </Box>

            <Divider />

            {/* Menu items. 「個人資料」曾因後端沒有自助更新端點而被移除（F-32）；
                PUT /auth/profile 補上後恢復（QA #2 / D1）。 */}
            <MenuItem
              onClick={() => {
                handleClose();
                navigate('/profile');
              }}
            >
              <ListItemIcon>
                <Person fontSize="small" />
              </ListItemIcon>
              個人資料
            </MenuItem>
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <Logout fontSize="small" />
              </ListItemIcon>
              登出
            </MenuItem>
          </Menu>
        </Box>
      </Toolbar>
    </AppBar>
  );
}
